import type { FirebaseApp } from 'firebase/app';
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from 'firebase/functions';
import type { User } from 'firebase/auth';

/**
 * LA PORTE D'ENTREE, COTE NAVIGATEUR.
 *
 * LE PROBLEME QU'ELLE REGLE, et il n'a pas de solution dans le navigateur.
 * Quelqu'un entre avec Google : Firebase signe un jeton, et ce jeton ne porte
 * AUCUN claim MyTaki. Sans `orgId`, les Security Rules lui refusent tout, y
 * compris la lecture de sa PROPRE fiche membre : l'app ne peut donc meme pas
 * VERIFIER s'il est invite. La question se pose au serveur, une fois, et le
 * serveur repond en posant les claims (ou en refusant).
 *
 * QUAND ELLE EST APPELEE, ET QUAND ELLE NE L'EST PAS. Uniquement quand le jeton
 * ne porte pas deja l'organisation de cette page. Une session etablie n'y
 * repasse jamais : c'est ce qui garantit qu'une Cloud Function indisponible ne
 * met personne dehors, et que l'entree quotidienne ne coute pas un appel.
 *
 * CE MODULE NE CONNAIT PAS `@taki/runtime-config`, comme le reste de ce package :
 * l'hote lui passe l'organisation et l'adresse de l'emulateur, qui ont
 * exactement la forme du bloc `emulators.functions` de `taki.config.json`.
 */

/**
 * Region des Cloud Functions. DOIT valoir celle de `functions/src/runtime.ts` :
 * viser la mauvaise region fait echouer l'appel sur un « Failed to fetch » qui
 * ne dit rien. `packages/sdk/tests/acces-contrat.test.mjs` compare les fichiers.
 */
export const ACCES_FUNCTIONS_REGION = 'europe-west1';

/** Version de la FORME des reponses attendue (recopie de functions/src/acces/contrat.ts). */
export const ACCES_CONTRACT_VERSION = 1;

/** Le nom de la callable qui garde la porte. */
export const CALLABLE_RATTACHEMENT = 'rattacherCompte';

/** Adresse de l'emulateur Functions, meme forme que `emulators.functions`. */
export interface FunctionsEmulatorEndpoint {
  host: string;
  port: number;
}

/**
 * Le verdict rendu a un compte qui se presente.
 *
 * `non-invite` n'est PAS une panne : c'est la reponse juste a quelqu'un qui
 * s'est authentifie sans que personne ne l'ait inscrit ici. L'ecran doit le
 * DIRE. `indisponible` est l'autre chose : la question n'a pas pu etre posee.
 */
export type StatutRattachement =
  | 'ok'
  | 'non-invite'
  | 'desactive'
  | 'sans-adresse'
  | 'indisponible';

/** Ce que le shell lit pour decider quoi montrer. */
export interface Rattachement {
  statut: StatutRattachement;
  /** L'organisation visee, pour pouvoir la nommer a l'ecran. */
  orgId: string;
  /** Le role retenu, seulement quand `statut` vaut `ok`. */
  orgRole: string | null;
  /** Le detail technique d'un `indisponible`, pour la console et le support. */
  raison: string | null;
}

/** Le reglage pose une fois au demarrage par l'hote. */
interface ReglageRattachement {
  app: FirebaseApp;
  orgId: string;
  emulator?: FunctionsEmulatorEndpoint | undefined;
}

let reglage: ReglageRattachement | null = null;
let functionsInstance: Functions | null = null;

/** Instances Functions deja branchees sur l'emulateur (une seule fois). */
const emulatorBoundFunctions = new WeakSet<Functions>();

/**
 * Pose le reglage de la porte. A appeler UNE fois au demarrage, juste apres
 * `initAuth`, avec la meme `FirebaseApp` que le SDK.
 *
 * Tant qu'il n'est pas appele, `rattacherCompteCourant` ne fait RIEN et rend
 * `null` : un hote qui ne veut pas de ce mecanisme n'a rien a desactiver.
 */
export function initRattachement(
  app: FirebaseApp,
  options: { orgId: string; emulator?: FunctionsEmulatorEndpoint | undefined },
): void {
  reglage = { app, orgId: options.orgId, ...(options.emulator ? { emulator: options.emulator } : {}) };
  functionsInstance = null;
}

/** L'organisation que cette page sert, ou `null` si la porte n'est pas reglee. */
export function orgDuRattachement(): string | null {
  return reglage?.orgId ?? null;
}

/** L'instance Functions, branchee sur l'emulateur une seule fois. */
function functionsDe(r: ReglageRattachement): Functions {
  if (functionsInstance === null) {
    functionsInstance = getFunctions(r.app, ACCES_FUNCTIONS_REGION);
  }
  const instance = functionsInstance;
  if (r.emulator && !emulatorBoundFunctions.has(instance)) {
    connectFunctionsEmulator(instance, r.emulator.host, r.emulator.port);
    emulatorBoundFunctions.add(instance);
    console.info(
      `[@taki/auth] Cloud Functions branchees sur l'emulateur ${r.emulator.host}:${r.emulator.port}.`,
    );
  }
  return instance;
}

/**
 * FAUT-IL FRAPPER A LA PORTE ? Fonction PURE, isolee pour etre jouable sans
 * navigateur et sans emulateur.
 *
 * Non si le jeton porte deja l'organisation de cette page : ses droits sont
 * etablis, le reste se joue dans les rules. Oui dans tous les autres cas, y
 * compris le desaccord d'organisation (un compte deplace d'une org a l'autre :
 * son jeton dit encore l'ancienne, et c'est la porte qui tranche).
 */
export function doitFrapper(orgAttendue: string | null, orgDuJeton: string | undefined): boolean {
  if (orgAttendue === null) return false;
  return orgDuJeton !== orgAttendue;
}

/**
 * Lit la reponse de la callable, sans lui faire confiance.
 *
 * Une reponse d'une autre VERSION de contrat n'est pas interpretee : le socle
 * et les Functions ne sont pas deployes ensemble (les Functions se deploient
 * a part, a la main), donc les deux peuvent se separer pour de vrai.
 */
function lireVerdict(brut: unknown, orgId: string): Rattachement {
  const data = typeof brut === 'object' && brut !== null ? (brut as Record<string, unknown>) : {};
  const version = data['contractVersion'];
  if (version !== ACCES_CONTRACT_VERSION) {
    return {
      statut: 'indisponible',
      orgId,
      orgRole: null,
      raison: `Les Cloud Functions repondent en version ${String(version)}, cette app attend la version ${ACCES_CONTRACT_VERSION}.`,
    };
  }
  const statut = data['statut'];
  if (
    statut !== 'ok' &&
    statut !== 'non-invite' &&
    statut !== 'desactive' &&
    statut !== 'sans-adresse'
  ) {
    return { statut: 'indisponible', orgId, orgRole: null, raison: `Verdict inconnu : ${String(statut)}.` };
  }
  return {
    statut,
    orgId,
    orgRole: typeof data['orgRole'] === 'string' ? data['orgRole'] : null,
    raison: null,
  };
}

/**
 * Frappe a la porte pour l'utilisateur donne, et rafraichit son jeton quand des
 * claims viennent d'etre poses.
 *
 * LE RAFRAICHISSEMENT N'EST PAS FACULTATIF : un jeton vit une heure en cache,
 * donc sans `getIdToken(true)` l'app continuerait de lire un jeton sans `orgId`
 * alors que le serveur vient de l'accorder, et tout serait refuse pendant une
 * heure sans que rien ne le dise.
 *
 * Rend `null` quand la porte n'est pas reglee (rien a faire, rien a dire).
 */
export async function rattacherCompteCourant(user: User): Promise<Rattachement | null> {
  const r = reglage;
  if (r === null) return null;

  try {
    const appeler = httpsCallable<{ orgId: string }, unknown>(
      functionsDe(r),
      CALLABLE_RATTACHEMENT,
    );
    const reponse = await appeler({ orgId: r.orgId });
    const verdict = lireVerdict(reponse.data, r.orgId);
    if (verdict.statut === 'ok') {
      await user.getIdToken(true);
    }
    return verdict;
  } catch (cause: unknown) {
    const message = cause instanceof Error ? cause.message : String(cause);
    console.error('[@taki/auth] la porte n a pas pu etre interrogee.', cause);
    return { statut: 'indisponible', orgId: r.orgId, orgRole: null, raison: message };
  }
}

/* -------------------------------------------------------------------------- */
/* La trace de connexion (journal d'audit, `docs/JOURNAL.md`)                  */
/* -------------------------------------------------------------------------- */

/** Le nom de la callable qui journalise une connexion. */
export const CALLABLE_CONNEXION = 'signalerConnexion';

/** Les connexions deja signalees par cette page (`uid:auth_time`). */
const connexionsSignalees = new Set<string>();

/**
 * « QUELQU'UN VIENT D'ENTRER » : demande au serveur de journaliser la
 * connexion, une fois les claims de l'org etablis.
 *
 * L'appel ne porte RIEN : le serveur lit l'uid, l'adresse, l'instant
 * d'authentification et le fournisseur dans le jeton signe, et son id de
 * document (`sha256(uid:auth_time)`) rend un second appel inoffensif. Cette
 * page n'appelle quand meme qu'une fois par authentification : plusieurs
 * composants montent `useUser`, et chacun recoit le meme utilisateur.
 *
 * N'ATTEND RIEN ET N'ECHOUE JAMAIS : une Function absente ou en panne ne doit
 * retarder ni empecher personne d'entrer. L'appelant ne fait pas `await`.
 */
export async function signalerConnexionCourante(user: User): Promise<void> {
  const r = reglage;
  if (r === null) return;
  try {
    const { authTime, claims } = await user.getIdTokenResult();
    if (claims['orgId'] !== r.orgId) return;
    const clef = `${user.uid}:${authTime}`;
    if (connexionsSignalees.has(clef)) return;
    connexionsSignalees.add(clef);
    await httpsCallable<Record<string, never>, unknown>(functionsDe(r), CALLABLE_CONNEXION)({});
  } catch (cause: unknown) {
    console.warn('[@taki/auth] la connexion n a pas pu etre journalisee.', cause);
  }
}
